import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import VendorSearchBar from './VendorSearchBar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import { useTranslate } from '@im/react-localization';
import dompurify from 'dompurify';
import AddMasterVendor from './components/AddMasterVendor';
import { useGetMasterVendorList } from '../hooks/create-master-vendor';
import { downloadFile } from 'src/util/downloadFileUtil';
import useSnackBar from 'src/hooks/useSnackBar';
import { useRoles } from 'src/provider/UserRoleProvider';

export default function SearchUI(prop: any) {
  const [searchParams] = useSearchParams();
  const translate = useTranslate();
  const { isCOGUser } = useRoles();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const [vendor, setVendor] = useState<string | undefined>();
  const [openAddVendor, setOpenAddVendor] = useState<boolean>(false);
  const downloadMasterVendor = useGetMasterVendorList();

  return (
    <>
      <Stack px={3} py={2}>
        <Box>
          <Typography fontSize={22} variant="h2">
            {translate('VendorProgram.VendorPrograms')}
          </Typography>
          <Typography sx={{ mt: 1 }} variant="body2">
            {translate('VendorProgram.FindVendor')}
          </Typography>
        </Box>
        <Box
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Typography
            data-testid="gds_vendor_program_name"
            mt={4}
            sx={(theme) => ({
              fontSize: theme.typography.body2.fontSize,
              fontWeight: theme.typography.fontWeightBold,
              lineHeight: theme.typography.body2.lineHeight,
            })}
            textAlign={'left'}
          >
            {translate('VendorProgram.VendorName')}
          </Typography>
          <VendorSearchBar
            onVendorSelected={(vendor: any) =>
              navigate(
                `/vendor-program/vendor-detail?vendorId=${vendor.vendorID}&countryCode=${countryCode}&companyCode=${companyCode}`
              )
            }
            setVendor={setVendor}
          />
        </Box>
        <Stack
          alignItems={'center'}
          direction="row"
          justifyContent={'flex-end'}
          mt={2}
          spacing={1}
        >
          <Button
            data-testid="gds-vp-ddt-button"
            href="/assets/DiscountTemplate.xlsx"
            variant="newText"
          >
            {translate('VendorProgram.DownloadDiscountTemplate')}
          </Button>
          <Button
            data-testid="gds-vp-vau-button"
            onClick={() =>
              navigate(
                `/vendor-program/view-all-uploads?countryCode=${countryCode}&companyCode=${companyCode}`
              )
            }
            variant="newOutlined"
          >
            {translate('VendorProgram.ViewAllUploads')}
          </Button>
          <Button
            data-testid="gds-vp-dmvl-button"
            onClick={() => {
              showSnackBar({
                autoHide: 'stay',
                message: translate('Common.Downloading'),
                severity: 'info',
              });
              downloadMasterVendor
                .mutateAsync()
                .then((res) => {
                  downloadFile(
                    res,
                    `${translate('VendorProgram.MasterVendorList')}-1.xlsx`
                  );
                  showSnackBar({
                    autoHide: 2000,
                    message: translate('Common.DownloadSuccess'),
                    severity: 'success',
                  });
                })
                .catch(() => {
                  showSnackBar({
                    autoHide: 2000,
                    message: translate('Common.DownloadError'),
                    severity: 'error',
                  });
                });
            }}
            variant="newText"
          >
            {translate('VendorProgram.DownloadMasterVendorList')}
          </Button>
          <Button
            data-testid="gds-vp-amv-button"
            onClick={() => setOpenAddVendor(true)}
            sx={{ display: isCOGUser ? 'inherit' : 'none' }}
            variant="newOutlined"
          >
            {translate('VendorProgram.AddMasterVendor')}
          </Button>
        </Stack>
        {!vendor && (
          <Box>
            <Typography
              mt={4}
              sx={(theme) => ({
                color: theme.palette.grey[500],
              })}
            >
              {translate('VendorProgram.SearchByVendor')}
            </Typography>
          </Box>
        )}
        {vendor && (
          <Box>
            <Typography
              component={'div'}
              dangerouslySetInnerHTML={{
                __html: dompurify.sanitize(
                  translate('VendorProgram.SearchNoResults', {
                    vendorName: vendor,
                  })
                ),
              }}
              mt={4}
              sx={(theme) => ({
                color: theme.palette.error[600],
              })}
            ></Typography>
          </Box>
        )}
      </Stack>
      {openAddVendor && (
        <AddMasterVendor handleClose={() => setOpenAddVendor(false)} />
      )}
    </>
  );
}
